/**
 * Derive the medication form's Dose / Dose unit / Route from a ScriptSure sig line.
 *
 * ScriptSure stamps a pre-built sig on every formulation it returns, composed from its own structured
 * directions (`Action / Quantity / Form / Route / Frequency`) — so the string carries all three fields
 * the form asks for, in clinically-reviewed vendor phrasing:
 *
 *     "4 Pre-filled Pen Syringe - Inject 2.5 milligram subcutaneously every seven days"
 *      └─ dispense prefix ──────┘ └verb┘ └dose┘ └─unit─┘ └───route───┘ └── frequency ──┘
 *
 * This is also the only signal available when the terminology carries no `dose_form_of` for the
 * product, which is every `[Zepbound]` concept (#5730).
 *
 * **Liquids are dosed by the concentration-independent unit** — the numerator of the strength, never
 * the container. Zepbound is `2.5 mg/0.5 mL`, so its sig doses in `mg`; U-100 insulin is
 * `100 unit/mL`, so its sig doses in `unit`. Reading a container count instead ("1 pen", "0.5 mL") is
 * how a concentration change turns into a silent dosing error, so the amount and unit are read
 * together from the text after the administration verb and concentrations are removed first.
 *
 * Both outputs map onto the form's existing closed vocabularies ({@link PATIENT_FRIENDLY_UNITS} and
 * {@link PATIENT_FRIENDLY_ROUTES}) rather than passing vendor text through, so a derived value is
 * always a selectable option and the composed sig stays in our own phrasing.
 *
 * **Deliberately conservative.** These values land on a prescription: a wrong route ("by mouth" on a
 * sublingual tablet) is a clinical error, not a cosmetic one. Every rule below requires an explicit
 * keyword, and anything unrecognized or contradictory yields `undefined` so the field stays empty for
 * the prescriber to fill. Guessing is never preferable to an empty field here.
 */

/**
 * Route keyword → patient-friendly route display, as spelled in {@link PATIENT_FRIENDLY_ROUTES}.
 *
 * Ordered most-specific-first: a sig saying "in the left eye" must not match the "eye" rule that
 * yields the both-sides "in each eye", and "sublingually" must beat the generic oral verb. Laterality
 * is preserved where the sig states it, since the sig is the one place a side is actually asserted.
 */
const ROUTE_RULES: readonly { pattern: RegExp; display: string }[] = [
  // Laterality first — a stated side must survive.
  { pattern: /\bin(?:to)? the left eye\b|\bleft eye\b/, display: 'in the left eye' },
  { pattern: /\bin(?:to)? the right eye\b|\bright eye\b/, display: 'in the right eye' },
  { pattern: /\bin(?:to)? the left ear\b|\bleft ear\b/, display: 'in the left ear' },
  { pattern: /\bin(?:to)? the right ear\b|\bright ear\b/, display: 'in the right ear' },
  { pattern: /\bleft nostril\b/, display: 'in the left nostril' },
  { pattern: /\bright nostril\b/, display: 'in the right nostril' },

  // Sublingual / buccal before oral: both are mouth-adjacent but pharmacologically distinct.
  { pattern: /\bsublingual(?:ly)?\b|\bunder the tongue\b/, display: 'under the tongue' },
  { pattern: /\bbuccal(?:ly)?\b|\bin the cheek\b|\bbetween cheek and gum\b/, display: 'in the cheek' },

  // `the` is optional throughout: ScriptSure writes the barer "in eye" / "in ear" ("Instill 1 drop in
  // eye every evening"), which an `in the eye`-only rule missed entirely, leaving Route blank.
  { pattern: /\bboth eyes\b|\beach eye\b|\bophthalmic(?:ally)?\b|\bin (?:the )?eyes?\b/, display: 'in each eye' },
  { pattern: /\bboth ears\b|\beach ear\b|\botic(?:ally)?\b|\bin (?:the )?ears?\b/, display: 'in each ear' },
  {
    pattern: /\beach nostril\b|\bboth nostrils\b|\bintranasal(?:ly)?\b|\bnasally\b|\bin (?:the )?nose\b/,
    display: 'in each nostril',
  },

  // Inhalation before oral: ScriptSure phrases an inhaler as "Inhale 2 inhalation **by mouth** daily",
  // which is anatomically true but means the respiratory tract, not swallowing. The oral rule would
  // otherwise win on the "by mouth" and mis-route every inhaler.
  { pattern: /\bby inhalation\b|\binhale\b|\bvia nebulizer\b|\bnebulized\b/, display: 'by inhalation' },
  { pattern: /\bby mouth\b|\borally\b|\bper os\b|\bp\.?o\.?\b/, display: 'by mouth' },
  {
    pattern: /\bsubcutaneous(?:ly)?\b|\bsub-?q\b|\bintramuscular(?:ly)?\b|\bby injection\b|\binject/,
    display: 'by injection',
  },
  { pattern: /\brectal(?:ly)?\b|\bper rectum\b/, display: 'rectally' },
  { pattern: /\bvaginal(?:ly)?\b|\bper vagina\b/, display: 'vaginally' },
  { pattern: /\btransdermal(?:ly)?\b|\bonto the skin\b/, display: 'onto the skin' },
  { pattern: /\bto the scalp\b/, display: 'to the scalp' },
  { pattern: /\btopical(?:ly)?\b|\bto the skin\b/, display: 'to the skin' },
  { pattern: /\bto the affected area\b|\bto affected area(?:s)?\b/, display: 'to the affected area' },
  { pattern: /\bswish and spit\b|\bas a rinse\b|\brinse\b/, display: 'as a rinse' },
  { pattern: /\bto the teeth\b|\bbrush\b/, display: 'to the teeth' },
];

/**
 * Dose-unit keyword → patient-friendly unit, as spelled in {@link PATIENT_FRIENDLY_UNITS}.
 *
 * Matched against the unit word the sig attaches to the dose amount, so these cover both the countable
 * dose forms ("take 1 **tablet**") and the strength units a liquid is dosed in ("inject 2.5
 * **milligram**"). Strength units come first: for a liquid they *are* the dose, and matching them ahead
 * of the container units stops "2.5 mg … pen injector" reading as a pen count.
 *
 * **This is not gated to liquids** — the rule is "report the unit the sig states", for every dose form.
 * So a solid whose sig is written by strength ("Take 10 mg by mouth") yields `mg`, while the same drug
 * written by count ("Take 1 tablet by mouth") yields `tablet`. That is deliberate: dosing a titratable
 * solid by strength is how levothyroxine and warfarin are legitimately prescribed, and rewriting the
 * prescriber's own framing into a tablet count would be the greater error — a strength holds when the
 * dispensed tablet size changes, a count does not. Gating on route would also require deciding which
 * framing is "right" per dose form, a clinical judgment that belongs in #5407, not in a regex.
 *
 * ScriptSure spells units out ("milligram", "milliliter"), so both the long and abbreviated forms are
 * matched.
 */
const UNIT_RULES: readonly { pattern: RegExp; unit: string }[] = [
  // Strength units first: for a liquid these *are* the dose ("Inject 2.5 milligram"), and matching
  // them ahead of the container units keeps "2.5 mg ... pen" from reading as a pen count.
  { pattern: /\bmicrograms?\b|\bmcgs?\b/, unit: 'mcg' },
  { pattern: /\bmilligrams?\b|\bmgs?\b/, unit: 'mg' },
  // ScriptSure counts inhaler doses as "inhalation" ("Inhale 2 inhalation by mouth daily"); the
  // patient-friendly table spells the same thing "puff".
  { pattern: /\bpuffs?\b|\bactuations?\b|\binhalations?\b/, unit: 'puff' },
  { pattern: /\bsprays?\b/, unit: 'spray' },
  { pattern: /\bdrops?\b|\bgtts?\b/, unit: 'drop' },
  { pattern: /\btablets?\b|\btabs?\b/, unit: 'tablet' },
  { pattern: /\bcapsules?\b|\bcaps?\b/, unit: 'capsule' },
  { pattern: /\bsuppositor(?:y|ies)\b/, unit: 'suppository' },
  { pattern: /\bpatch(?:es)?\b/, unit: 'patch' },
  { pattern: /\blozenges?\b|\btroches?\b/, unit: 'lozenge' },
  { pattern: /\bfilms?\b/, unit: 'film' },
  { pattern: /\bwafers?\b/, unit: 'wafer' },
  { pattern: /\bpackets?\b|\bsachets?\b/, unit: 'packet' },
  { pattern: /\bpads?\b/, unit: 'pad' },
  { pattern: /\binserts?\b/, unit: 'insert' },
  { pattern: /\bpellets?\b/, unit: 'pellet' },
  { pattern: /\bflakes?\b/, unit: 'flake' },
  { pattern: /\bpieces?\b/, unit: 'piece' },
  { pattern: /\bpacks?\b/, unit: 'pack' },
  { pattern: /\bapplications?\b|\bapply\b/, unit: 'application' },
  // Metered injectors dose in device units — ScriptSure says "unit", "pen", or "dose".
  { pattern: /\bunits?\b|\bpens?\b/, unit: 'unit' },
  { pattern: /\bdoses?\b/, unit: 'dose' },
  { pattern: /\bmL\b|\bmilliliters?\b|\bteaspoonfuls?\b|\btablespoonfuls?\b/, unit: 'mL' },
];

/**
 * Every route display {@link parseSigDosage} can produce, deduped in table order.
 *
 * Exported so a test can assert each one is actually reachable from some sig. Deriving that list from
 * `PATIENT_FRIENDLY_ROUTES` instead cannot work: the parser only produces a *subset* of those (a dose
 * form has laterality variants and routes no sig phrasing targets), so the test would have no way to
 * tell "no rule produces this" from "no rule should". The rules are the authority on what is
 * reachable, so the rules are what gets exported — the regexes stay private.
 */
export const SIG_ROUTE_DISPLAYS: readonly string[] = [...new Set(ROUTE_RULES.map((rule) => rule.display))];

/** Every dose unit {@link parseSigDosage} can produce, deduped in table order. Same purpose as
 * {@link SIG_ROUTE_DISPLAYS}. */
export const SIG_DOSE_UNITS: readonly string[] = [...new Set(UNIT_RULES.map((rule) => rule.unit))];

export interface SigDosage {
  /** Per-dose amount ("Take 2 tablets" → 2), or undefined when the sig states no dose. */
  dose: number | undefined;
  /** Patient-friendly dose unit, or undefined when the sig names none recognizably. */
  units: string | undefined;
  /** Patient-friendly route display, or undefined when the sig names none recognizably. */
  route: string | undefined;
}

/**
 * **Concentration** expressions only — "100 unit/mL", "2.5 mg/0.5 mL", "0.3 %".
 *
 * A ratio describes how much drug is in the container, so it is never what the patient administers and
 * must not be read as the dose. A *bare* amount is the opposite: for a liquid it is exactly the dose
 * ScriptSure states ("Inject 2.5 milligram subcutaneously"), so bare strengths are deliberately left
 * intact — stripping them is what previously discarded the dose and left the parser guessing a
 * container count instead (#5730).
 *
 * `unt` is not a typo for `unit`: it is RxNorm's own abbreviation for the potency unit, as it appears
 * in product names like "3 ML insulin lispro 200 UNT/ML Pen Injector". Both spellings reach this
 * parser — the sig from ScriptSure and the label from the RxNorm catalog — so both are matched.
 */
const CONCENTRATION_RE =
  /\b\d+(?:\.\d+)?\s*(?:mcg|microgram|mg|milligram|g|gram|unt|units?|iu|meq|mmol|%)\s*\/\s*\d*(?:\.\d+)?\s*(?:mcg|mg|g|ml|milliliter|unt|units?|hr|hour|actuation|spray|dose|puff)s?/gi;
/** A standalone percentage strength ("1 % cream") — a concentration with no denominator spelled out. */
const PERCENT_STRENGTH_RE = /\b\d+(?:\.\d+)?\s*%/g;
/** A parenthesised amount restating the strength as a volume ("Inject 2.5 mg (0.5 mL) subcutaneously").
 * It describes the same dose the strength already gave, so it is not a countable dose the patient
 * administers — dropped whole, else "0.5 mL" would read as the dose unit. */
const PARENTHESISED_AMOUNT_RE = /\(\s*\d+(?:\.\d+)?\s*(?:ml|milliliters?|mcg|mg|g|units?|unt)\s*\)/gi;

/** The administration verbs, matched anywhere in the sig so a dispense prefix is skipped. */
const SIG_DOSE_VERB_AFTER_RE = /\b(?:take|apply|use|inject|inhale|instill|insert|chew|dissolve|spray|place|swallow)\b/;
/** `<amount>[-<high>] <unit-word>` immediately after the verb. The unit word is optional so a bare
 * "Take 1 by mouth" still yields an amount. */
const DOSE_AMOUNT_AND_UNIT_RE = /^\s+(\d+(?:\.\d+)?)(?:\s*(?:-|to)\s*(\d+(?:\.\d+)?))?\s*([A-Za-z]+)?/;

/**
 * The dose amount and unit a sig states after its administration verb.
 *
 * ScriptSure writes the directions as `<verb> <amount> <unit> <route> <frequency>`, optionally behind
 * a dispense prefix: **"4 Pre-filled Pen Syringe - Inject 2.5 milligram subcutaneously every seven
 * days"**. Anchoring on the verb is what skips that prefix — reading the first number in the string
 * would take the dispense count (4) as the dose.
 *
 * The unit is whatever immediately follows the amount, so a liquid's `milligram` is read as the dose
 * unit rather than discarded as a strength. Returns `undefined` when the sig states no amount after a
 * verb, which includes ScriptSure's blank templates ("Inject __ milligram …").
 */
function doseFromVerb(sigLine: string): { amount: number; unitWord: string } | undefined {
  const verb = SIG_DOSE_VERB_AFTER_RE.exec(sigLine.toLowerCase());
  if (verb?.index === undefined) return undefined;
  const after = sigLine.slice(verb.index + verb[0].length);
  const stated = DOSE_AMOUNT_AND_UNIT_RE.exec(after);
  if (!stated) return undefined;
  // A range doses at the high end ("1-2 tablets" → 2), matching `inferPerDoseFromSig`.
  const low = Number.parseFloat(stated[1]!);
  const high = stated[2] ? Number.parseFloat(stated[2]) : low;
  const amount = Number.isFinite(high) && high > 0 ? high : low;
  return Number.isFinite(amount) ? { amount, unitWord: stated[3] ?? '' } : undefined;
}

/**
 * Parse a ScriptSure sig line into the form's Dose / Dose unit / Route.
 *
 * Returns `undefined` for any field the sig doesn't state unambiguously — the caller should leave such
 * a field empty rather than substitute a default.
 *
 * **Dose and unit are read together from the text after the administration verb**, which is where
 * ScriptSure states them. For a liquid that unit is a strength unit and that is correct: the sig must
 * name the *concentration-independent* unit — the numerator of the strength, not the container. So
 * "Inject 2.5 milligram subcutaneously every seven days" yields `2.5` / `mg`, and 10 units of U-100
 * insulin yields `10` / `unit`, never "1 pen" or "0.5 mL". Dosing by container is how a concentration
 * change becomes a silent dosing error.
 *
 * Concentrations are removed first, so "Inject 100 unit/mL, 10 units" doses 10 rather than 100.
 */
export function parseSigDosage(sigLine: string | undefined): SigDosage {
  if (!sigLine?.trim()) {
    return { dose: undefined, units: undefined, route: undefined };
  }

  const raw = sigLine.toLowerCase();
  const withoutConcentration = sigLine
    .replace(PARENTHESISED_AMOUNT_RE, ' ')
    .replace(CONCENTRATION_RE, ' ')
    .replace(PERCENT_STRENGTH_RE, ' ');

  const stated = doseFromVerb(withoutConcentration);

  // Prefer the unit the sig attached to the dose amount; fall back to scanning the whole sig, which
  // catches phrasings that separate the two ("Apply topically ... 1 patch").
  const units =
    (stated?.unitWord ? UNIT_RULES.find((rule) => rule.pattern.test(stated.unitWord))?.unit : undefined) ??
    UNIT_RULES.find((rule) => rule.pattern.test(withoutConcentration))?.unit;

  // A patch is transdermal, which the table spells "onto the skin" — sigs say the looser "to the
  // skin"/"topically", which for every other dose form means the topical route. The unit is what
  // separates them, so let it override once matched.
  const matchedRoute = ROUTE_RULES.find((rule) => rule.pattern.test(raw))?.display;
  const route = units === 'patch' && matchedRoute === 'to the skin' ? 'onto the skin' : matchedRoute;

  // Only report a dose when the sig actually stated an amount. `inferPerDoseFromSig` defaults to 1 on
  // a verb with no number, which would turn a blank template ("Inject __ milligram") into a real dose.
  return { dose: stated?.amount, units, route };
}
